import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import type { ComponentProps, ElementType } from "react";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-amber text-on-accent hover:bg-amber-warm",
        secondary:
          "border border-border-subtle text-text-secondary hover:bg-bg-tertiary",
        destructive: "bg-red-muted text-white hover:bg-red-600",
        "destructive-outline":
          "border border-red-muted text-red-muted hover:bg-red-muted hover:text-white",
        ghost:
          "border border-border-subtle text-text-secondary hover:border-text-secondary hover:text-text-primary",
        player:
          "rounded-full bg-bg-tertiary text-text-secondary hover:bg-amber/20 hover:text-amber",
        "player-active":
          "rounded-full bg-amber text-on-accent hover:bg-amber-warm",
      },
      size: {
        default: "px-3 py-1.5",
        sm: "px-2 py-1 text-xs",
        lg: "px-4 py-2",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

/** Lucide icon component type */
type LucideIcon = ElementType<{ className?: string; "aria-hidden"?: boolean }>;

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Lucide icon component to render on the left side. Size scales with font. */
    leftIcon?: LucideIcon;
    /** Additional class name for the left icon (e.g., custom colors, animations) */
    leftIconClassName?: string;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  leftIcon: LeftIcon,
  leftIconClassName,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  // When asChild is true, Slot expects a single child element.
  // The leftIcon prop is not supported with asChild - use children instead.
  if (asChild) {
    return (
      <Comp
        className={clsx(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      type="button"
      className={clsx(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {LeftIcon && (
        <LeftIcon
          aria-hidden
          className={clsx("shrink-0 size-[1.15em]", leftIconClassName)}
        />
      )}
      {children}
    </Comp>
  );
}

export { Button };
